import React from "react";
import { Modal } from "react-bootstrap";
import { Exclamation } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

const ClaimIssueModal = ({ show, handleClose, claim }) => {
  const { viewClaimInfo } = useSelector((state) => state.claim);

  // === 1. Parse NPHIES Validation Errors from provider_request.valdn_err ===
  const parseValidationErrors = () => {
    const valdn_err = viewClaimInfo?.provider_request?.valdn_err;
    if (!valdn_err) return [];

    const errors = [];

    const processSection = (data, sectionName) => {
      if (!data || typeof data !== "object") return;

      // Helper: Format message (handle array of strings)
      const formatMsg = (msg, label) => {
        if (Array.isArray(msg)) {
          return (
            <div>
              {label && <span className="fw-bold d-block mb-1">{label}:</span>}
              <ol className="ps-3 mb-0">
                {msg.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ol>
            </div>
          );
        }
        return label ? `${label}: ${msg}` : msg;
      };

      // Case 1: Simple object with .msg (pract, diagnosis, prescrip, etc.)
      if (data.msg) {
        const displayName =
          {
            pract: 'Principal Practitioner',
            diag: 'Diagnosis',
            diagnosis: 'Diagnosis',
            careTeam: 'Care Team',
            supportingInfo: 'Supporting Info',
            prescrip: 'Prescription'
          }[sectionName] || sectionName.charAt(0).toUpperCase() + sectionName.slice(1);

        errors.push({
          type: 'validation',
          field: displayName,
          message: formatMsg(data.msg, displayName)
        });
        return;
      }

      // Case 2: Array (like service[])
      if (Array.isArray(data)) {
        let currentLineNo = null;

        data.forEach((item, index) => {
          if (item.no !== undefined) {
            // Save line number
            currentLineNo = item.no;
          }
          if (item.msg) {
            // Found message - use the saved line number or the current index
            const line = currentLineNo !== null ? currentLineNo : index + 1;
            const sectionLabel =
              sectionName === 'service'
                ? 'Service'
                : sectionName.charAt(0).toUpperCase() + sectionName.slice(1);

            errors.push({
              type: 'validation',
              field: null,
              message: formatMsg(item.msg, `In ${sectionLabel} No. ${line}`)
            });
            // Reset line number after using it
            currentLineNo = null;
          }
        });

        return;
      }

      // Case 3: Nested object → recurse
      Object.keys(data).forEach((key) => {
        processSection(data[key], key);
      });
    };

    // Start processing from root
    Object.keys(valdn_err).forEach((key) => {
      processSection(valdn_err[key], key);
    });

    return errors;
  };

  const validationErrors = parseValidationErrors();

  // === 2. Get Payer Response Errors from viewClaimInfo ===
  const isTag = viewClaimInfo?.payer_response?.tag;
  const errorRemarkDetails = viewClaimInfo?.payer_response?.remarks || [];
  const disposition = viewClaimInfo?.payer_response?.disposition;

  // === Check if any error exists ===
  const hasErrors =
    validationErrors.length > 0 ||
    errorRemarkDetails.length > 0 ||
    !!disposition;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="text-danger">
        <Modal.Title className="fs-11 d-flex text-danger align-items-center">
          <Exclamation className="me-2 fs-7" />
          Claim Issues - {claim?.claimfull_id || "Unknown"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {!hasErrors ? (
          <div className="text-muted">No issues found</div>
        ) : (
          <div className="space-y-4">
            {/* NPHIES Validation Errors */}
            {validationErrors.length > 0 && (
              <div>
                <h6 className="text-danger fw-bold border-bottom pb-2 fs-10 mb-3">
                  NPHIES Validation Errors
                </h6>
                <ul className="list-unstyled mb-0">
                  {validationErrors.map((err, idx) => (
                    <li key={idx} className="text-danger mb-2 fs-11">
                      {err.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ClaimResponse Errors */}
            {(errorRemarkDetails.length > 0 || disposition) && (
              <div>
                <h6 className="text-danger fs-11 fw-bold border-bottom pb-2 mb-3">
                  {isTag ? "NPHIES" : "Payer"} Processing Errors
                </h6>
                <ul className="list-unstyled mb-0">
                  {errorRemarkDetails.map((item, index) => (
                    <li
                      key={`error-${index}`}
                      className="text-danger  fs-11 mb-2"
                    >
                      <span className="fw-bold">
                        {isTag ? "(NPHIES)" : "(PAYER)"}:
                      </span>{" "}
                      {item?.claimerr_code
                        ? `(${item?.claimerr_code}) - ${item?.claimerr_display}`
                        : item?.claimerr_display}
                    </li>
                  ))}
                  {disposition && (
                    <li className="text-danger mb-2">
                      <span className="fw-bold fs-11">
                        {isTag ? "(NPHIES)" : "(PAYER)"} Disposition:
                      </span>{" "}
                      {disposition}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

ClaimIssueModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  claim: PropTypes.shape({
    claimfull_id: PropTypes.string,
  }),
};

export default ClaimIssueModal;
